import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { TreatmentManager } from '@/components/treatment-manager/TreatmentManager';
import { getCustomers } from '@/services/dataService';
import type { Customer } from '@/services/dataService';
import { TreatmentService } from '@/services/treatmentService';
import { TreatmentRecord } from '@/types/treatment';
import { usePageTitle } from '@/hooks/usePageTitle';

const TreatmentDetail: React.FC = () => {
  usePageTitle('施術記録詳細');
  const { id, tid } = useParams<{ id: string; tid: string }>();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [treatment, setTreatment] = useState<TreatmentRecord | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const loadData = useCallback(async () => {
    try {
      // Load customer data - Issue #237: DataService経由でSupabaseから取得
      const customers = await getCustomers();
      const foundCustomer = customers.find(c => c.id === parseInt(id!));
      
      if (!foundCustomer) {
        navigate('/customers');
        return;
      }
      
      setCustomer(foundCustomer);
      
      // Load treatment data
      const treatments = await TreatmentService.getTreatmentRecords(parseInt(id!));
      const foundTreatment = treatments.find((t: any) => t.id === tid);
      
      if (!foundTreatment) {
        navigate(`/customers/${id}`);
        return;
      }
      
      setTreatment(foundTreatment);
    } catch (error) {
      console.error('Error loading data:', error);
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  }, [id, tid, navigate]);
  
  useEffect(() => {
    if (id && tid) {
      loadData();
    }
  }, [id, tid, loadData]);
  
  const handleBack = () => {
    navigate(`/customers/${id}`);
  };
  
  const handleEdit = () => {
    setIsEditing(true);
  };
  
  const handleCancel = () => {
    if (isEditing && confirm('編集中の内容は保存されません。よろしいですか？')) {
      setIsEditing(false);
      loadData(); // Reload original data
    }
  };
  
  const handleSave = () => {
    setIsEditing(false);
    // Save will be handled by TreatmentManager
    setTimeout(() => {
      loadData(); // Reload updated data
    }, 100);
  };
  
  const handleDelete = () => {
    if (confirm('この施術記録を削除してもよろしいですか？')) {
      try {
        TreatmentService.deleteTreatmentRecord(parseInt(id!), tid!);
        navigate(`/customers/${id}`);
      } catch (error) {
        console.error('Error deleting treatment:', error);
        alert('削除に失敗しました');
      }
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }
  
  if (!customer || !treatment) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>データが見つかりません</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} size="large">
              <ArrowBackIcon />
            </IconButton>
            
            <Breadcrumbs>
              <Link 
                component="button"
                variant="body1"
                onClick={() => navigate('/customers')}
                underline="hover"
                color="inherit"
              >
                顧客管理
              </Link>
              <Link 
                component="button"
                variant="body1"
                onClick={handleBack}
                underline="hover"
                color="inherit"
              >
                {customer.name}様
              </Link>
              <Typography color="text.primary">
                施術記録詳細
              </Typography>
            </Breadcrumbs>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                >
                  キャンセル
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                >
                  保存
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                >
                  削除
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  編集
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Paper>
      
      {/* Main Content */}
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Paper 
          elevation={1}
          sx={{ 
            p: 3,
            borderRadius: 2,
            minHeight: '600px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="h5">
              {customer.name}様の施術記録
            </Typography>
            <Chip 
              label={new Date(treatment.date).toLocaleDateString('ja-JP')}
              color="primary"
              variant="outlined"
            />
          </Box>
          
          <TreatmentManager
            customerId={customer.id}
            mode={isEditing ? 'edit' : 'view'}
            initialData={{
              customerName: customer.name,
              staffName: treatment.staffName,
              date: new Date(treatment.date),
              physicalCondition: treatment.physicalCondition || '良好',
              symptoms: treatment.symptoms || [],
              treatmentMenu: treatment.treatmentMenu,
              treatmentDuration: treatment.treatmentDuration,
              // Additional properties for TreatmentManager compatibility
            }}
            // treatmentId and onSaveSuccess handled internally by TreatmentManager
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default TreatmentDetail;